/**
 * BillingListFilterBar — search + filter drawer + active-filter chips for the
 * Patient Billing sub-tab lists (Invoices / Advance receipts / Refunds).
 *
 * Flutter ref: `patient_invoices_filter_widget.dart` (the sidebar) +
 * `patient_invoices_list_screen.dart` (search box + filter tags). Flutter
 * surfaces this per sub-tab:
 *   - Invoices  → search "Search by doctor, invoice #" + Time Period +
 *                 Sponsor (Self-pay/Insurance/Corporate) + Payment Status
 *                 (N/A / Paid in Full / Partially paid / Not Paid)
 *   - Receipts  → search "Search by receipt #" + Time Period only
 *   - Refunds   → search + Time Period + Method (Cash/Card/Bank)
 *
 * Config-driven: the caller passes the search placeholder and the facet
 * checkbox groups it needs. Date range is always available. Facet option
 * VALUES are the verbatim Flutter `app_constants.dart` strings (magic-string
 * rule) — the caller supplies them.
 *
 * React idiom (CLAUDE.md): canvas sidebar → Signal DS `Drawer`; date range →
 * Calendar (range) in a Popover; checkboxes → Signal DS `Checkbox`.
 */

import { useEffect, useState } from 'react';
import { Filter, Search, X } from 'lucide-react';
import {
  Button,
  Tag,
  DateRangePicker,
  type DateRange,
  Drawer,
  Checkbox,
} from '@scripsteam/scrips-signal-ds';
import { TextField } from '@scripsteam/scrips-signal-ds';
import { useDebounce } from '@/shared/hooks/use-debounce';

export interface BillingFacetGroup {
  /** Filter key the caller maps to a request field. */
  key: string;
  title: string;
  options: { value: string; label: string }[];
}

export interface BillingFilterValue {
  search: string;
  /** Local-midnight ISO (no Z), or null. */
  startDate: string | null;
  endDate: string | null;
  /** facet key → selected values. */
  facets: Record<string, string[]>;
}

export const EMPTY_BILLING_FILTER: BillingFilterValue = {
  search: '',
  startDate: null,
  endDate: null,
  facets: {},
};

interface BillingListFilterBarProps {
  searchPlaceholder: string;
  facetGroups?: BillingFacetGroup[];
  value: BillingFilterValue;
  onChange: (next: BillingFilterValue) => void;
}

const fmtDate = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
      });
};

/** Local-midnight ISO without the `Z` suffix (.NET binder, CLAUDE.md). */
const toLocalIso = (d: Date): string => {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T00:00:00`;
};

export function BillingListFilterBar({
  searchPlaceholder,
  facetGroups = [],
  value,
  onChange,
}: BillingListFilterBarProps) {
  // PROD-2743 · switch ON: DS `search` / `close` glyphs at the 16px inline step. OFF = today.
  const [sheetOpen, setSheetOpen] = useState(false);

  // Local search box → debounced into the filter value so each keystroke
  // doesn't refetch (Flutter debounces 500ms; we use 400 like the patient list).
  const [searchText, setSearchText] = useState(value.search);
  const debounced = useDebounce(searchText, 400);
  useEffect(() => {
    if (debounced !== value.search) onChange({ ...value, search: debounced });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  const set = (patch: Partial<BillingFilterValue>) =>
    onChange({ ...value, ...patch });

  const toggleFacet = (key: string, optValue: string) => {
    const cur = value.facets[key] ?? [];
    const next = cur.includes(optValue)
      ? cur.filter((v) => v !== optValue)
      : [...cur, optValue];
    set({ facets: { ...value.facets, [key]: next } });
  };

  // Always a defined DateRange (never `undefined`) so DS `DateRangePicker`
  // stays fully controlled — passing `undefined` makes it fall back to its
  // own uncontrolled `internal` state, which does not clear on Reset / Reset
  // All (the old fully-controlled `Calendar` cleared correctly on both).
  const dateRange: DateRange = value.startDate
    ? {
        from: new Date(value.startDate),
        to: value.endDate ? new Date(value.endDate) : undefined,
      }
    : { from: undefined };

  const anyFacet = Object.values(value.facets).some((v) => v.length > 0);
  const anyActive = !!value.startDate || !!value.endDate || anyFacet;

  const labelFor = (key: string, val: string) =>
    facetGroups
      .find((g) => g.key === key)
      ?.options.find((o) => o.value === val)?.label ?? val;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <div className="relative max-w-xs flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-text-hint" />
          <TextField
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder={searchPlaceholder}
            inputClassName="h-8 pl-8 text-sm"
          />
          {searchText ? (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => setSearchText('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-text-hint hover:text-text-primary"
            >
              <X className="size-4" />
            </button>
          ) : null}
        </div>

        <div className="flex-1" />

        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={() => setSheetOpen(true)}
          icon={<Filter className="size-4" aria-hidden />}
          iconPosition="leading"
        >
          Filters
        </Button>

        <Drawer
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          side="right"
          width={360}
          title="Filters"
          className="overflow-y-auto scrollbar-hide"
          headerActions={
            <button
              type="button"
              disabled={!anyActive}
              data-slot="billing-filters-reset-all"
              onClick={() => set({ startDate: null, endDate: null, facets: {} })}
            >
              Reset All
            </button>
          }
        >
            <div data-slot="billing-filters-body">
              <Section
                title="Time Period"
                onReset={() => set({ startDate: null, endDate: null })}
                resetEnabled={!!value.startDate || !!value.endDate}
              >
                <DateRangePicker
                  size="compact"
                  placeholder="Select date range"
                  value={dateRange}
                  onChange={(r) =>
                    set({
                      startDate: r?.from ? toLocalIso(r.from) : null,
                      endDate: r?.to ? toLocalIso(r.to) : null,
                    })
                  }
                />
              </Section>

              {facetGroups.map((group) => (
                <Section
                  key={group.key}
                  title={group.title}
                  onReset={() =>
                    set({ facets: { ...value.facets, [group.key]: [] } })
                  }
                  resetEnabled={(value.facets[group.key] ?? []).length > 0}
                >
                  {group.options.map((opt) => (
                    <Checkbox
                      key={opt.value}
                      checked={(value.facets[group.key] ?? []).includes(
                        opt.value,
                      )}
                      onCheckedChange={() => toggleFacet(group.key, opt.value)}
                      label={
                        <span data-slot="billing-filters-option">
                          {opt.label}
                        </span>
                      }
                      className="gap-2"
                    />
                  ))}
                </Section>
              ))}
            </div>
        </Drawer>
      </div>

      {anyActive ? (
        <div className="flex flex-wrap gap-2">
          {value.startDate ? (
            <Tag
              removeLabel={`Remove ${fmtDate(value.startDate)}${
                value.endDate ? ` - ${fmtDate(value.endDate)}` : ''
              }`}
              onRemove={() => set({ startDate: null, endDate: null })}
            >
              {`${fmtDate(value.startDate)}${
                value.endDate ? ` - ${fmtDate(value.endDate)}` : ''
              }`}
            </Tag>
          ) : null}
          {Object.entries(value.facets).flatMap(([key, vals]) =>
            vals.map((v) => (
              <Tag
                key={`${key}:${v}`}
                removeLabel={`Remove ${labelFor(key, v)}`}
                onRemove={() => toggleFacet(key, v)}
              >
                {labelFor(key, v)}
              </Tag>
            )),
          )}
        </div>
      ) : null}
    </div>
  );
}

function Section({
  title,
  onReset,
  resetEnabled,
  children,
}: {
  title: string;
  onReset: () => void;
  resetEnabled: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div data-slot="billing-filters-section-head">
        <span data-slot="billing-filters-section-title">
          {title}
        </span>
        <button
          type="button"
          disabled={!resetEnabled}
          onClick={onReset}
          data-slot="billing-filters-reset"
        >
          Reset
        </button>
      </div>
      <div data-slot="billing-filters-section-body">{children}</div>
    </div>
  );
}

